import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, switchMap } from 'rxjs';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { Language } from '../../../../interfaces/cached-content';
import { GbTheme, THEME_SIGNAL } from '../../../../interfaces/theme';
import { CacheService } from '../../../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../../../ssr-services/subscriber/subscriber.service';

@Component({
  selector: 'app-select-language',
  imports: [],
  templateUrl: './select-language.component.html',
  styleUrl: './select-language.component.scss',
})
export class SelectLanguageComponent implements OnInit {
  public theme = inject(THEME_SIGNAL);

  public GbTheme = GbTheme;

  private readonly translateService = inject(TranslateService);

  selectedLanguageCode = signal<string | null>(null);

  public subscriberService = inject(SubscriberService);

  public cacheService = inject(CacheService);

  public languageSelected = output();

  public toasterService = inject(ToastService);

  public allLanguages = computed<Language[]>(() => {
    const cacheContent = this.cacheService.cachedContent();

    if (!cacheContent) {
      return [];
    }

    return cacheContent.cache.config.languages.supported;
  });

  public isRtl = computed(() => {
    const subscriberLanguage = this.subscriberService.subscriberLanguage();
    return subscriberLanguage.RTL;
  });

  public ngOnInit(): void {
    const subscriberLanguage = this.subscriberService.subscriberLanguage();
    this.selectedLanguageCode.set(subscriberLanguage.code);
  }

  /**
   * Select the language
   * @param languageCode
   * @returns
   */
  public selectLanguage(languageCode: string): Observable<boolean> {
    const originalLanguageName = structuredClone(this.subscriberService.subscriberLanguage()).name;
    this.selectedLanguageCode.set(languageCode);
    return this.subscriberService.updateLanguage(languageCode).pipe(
      switchMap(() => {
        const newLanguageName = this.subscriberService.subscriberLanguage().name;
        return this.translateService.use(languageCode).pipe(
          map(() => {
            this.languageSelected.emit();
            this.toasterService
              .openSuccessToast(
                `${this.translateService.instant('language.changed_successfully')} `,
                {
                  specialContent: this.isRtl()
                    ? `${newLanguageName} - ${originalLanguageName}`
                    : `${originalLanguageName} - ${newLanguageName}`,
                }
              )
              .pipe(switchMap((toast) => toast.present()))
              .subscribe();
            return true;
          })
        );
      })
    );
  }
}
